import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Opens every new page at its top.
 *
 * A router navigation swaps the page's content but leaves the window's scroll
 * where the previous page had it, so `/pricing` opened wherever `/` was
 * scrolled to. react-router's own `ScrollRestoration` needs a data router, and
 * the app mounts a `BrowserRouter`, so this does the part that matters, the way
 * `ScrollRestoration` does it:
 *
 * - back and forward (`POP`) are left to the browser, which puts the reader
 *   back where they were;
 * - a link with a `#…` scrolls to that element — a router link goes through
 *   `history.pushState`, and browsers do not scroll to the fragment for it —
 *   and to the top if the page has no such element;
 * - a change of the query string alone — a filter, a tab — keeps the reader
 *   where they are;
 * - following a link to the page you are already on goes back to its top, as
 *   a full page load would.
 */
export function ScrollToTop(): null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef(location);

  // Before paint, so the new page is never drawn at the old position first.
  // It runs on every new history entry (`key`), then decides from what changed.
  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = location;
    if (location.key === before.key || navigationType === 'POP') return;

    const samePlace = location.pathname === before.pathname && location.hash === before.hash;
    if (samePlace && location.search !== before.search) return;

    const target = location.hash
      ? document.getElementById(decodeURIComponent(location.hash.slice(1)))
      : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [location, navigationType]);

  return null;
}
